<script setup>
import {usePage} from "@inertiajs/inertia-vue3";
import {useHelpers, useTenantTextRules} from "@/helpers";
import {computed, ref, watch} from "vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import EditProjectElementForm from "@/Components/Admin/Project/Element/EditProjectElementForm.vue";
import DialogModal from "@/Components/DialogModal.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import {Inertia} from "@inertiajs/inertia";
import InputLabel from "@/Components/InputLabel.vue";
import SelectInput from "@/Components/SelectInput.vue";

const props = defineProps({
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  },
  elementPackages: Array,
  elementTypes: Array,
  vendors: Array,
  isClosedProject: {
    type: Boolean,
    default: false,
  },
});
const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;

const selectedPackages = ref([]);
project.element_packages.forEach(elementPackage => {
  selectedPackages.value.push(elementPackage.pack_id);
});
watch(projectProperty, (value) => {
  project = projectProperty.value;
  selectedPackages.value = [];
  project.element_packages.forEach(elementPackage => {
    selectedPackages.value.push(elementPackage.pack_id);
  });
});
const elementImageUrl = (fileName) => {
  return `/content/uploadFiles/element/${fileName}`;
};

const saving = ref(false);
const clickedSaveForm = ref(false);

const saveForm = () => {
  saving.value = true;
  clickedSaveForm.value = true;
}

const updateSaving = (value) => {
  clickedSaveForm.value = value.loading;
  saving.value = value.loading;

  if (editing.value && (!value.hasError && !value.loading)) {
    closeEditElementModal();
  }
}

const preview = ref(null);

const openPreviewModal = (image) => {
  preview.value = image;
}

const closePreviewModal = () => {
  preview.value = null;
}

const editing = ref(null);
const openEditElementModal = (element) => {
  editing.value = element;
}

const closeEditElementModal = () => {
  editing.value = null;
}

const deletingPackage = ref(null);
const openDeletePackageModal = (elementPackage) => {
  deletingPackage.value = elementPackage;
}

const closeDeletePackageModal = () => {
  deletingPackage.value = null;
}
const deleteElementPackage = (elementPackage) => {
  Inertia.post(route('admin.projects.element-packages.destroy', {
    project: project.pid,
    package: elementPackage.pack_id
  }), {_method: 'DELETE'}, {
    preserveScroll: true,
    onSuccess() {
      closeDeletePackageModal();
    }
  });
}

const deletingElement = ref(null);
const openDeleteElementModal = (element) => {
  deletingElement.value = element;
}

const closeDeleteElementModal = () => {
  deletingElement.value = null;
}

const deleteElement = (element) => {
  Inertia.post(route('admin.projects.elements.destroy', {
    projectid: project.pid,
    elementid: element.prj_element_id
  }), {_method: 'DELETE'}, {
    preserveScroll: true,
    onSuccess() {
      closeDeleteElementModal();
    }
  });
}

const newPackage = ref(null);

const addNewPackage = () => {
  if (newPackage.value === null) {
    return;
  }

  Inertia.post(route('admin.projects.element-packages.store', {
    project: project.pid,
  }), {package_id: newPackage.value}, {
    preserveScroll: true,
    onSuccess() {
      newPackage.value = null;
    }
  });
}
const repairElementsLabel = computed(() =>
  resolveTextRule('project.elements.section_title', 'Elements', 'project.elements.section_title')
);
const repairTypeLabel = computed(() =>
  resolveTextRule('project.elements.table.type_column', 'Type', 'project.elements.table.type_column')
);
const styleSkuLabel = computed(() =>
  resolveTextRule('project.elements.table.style', 'Style/SKU', 'project.elements.table.style')
);
const costPerUnitLabel = computed(() =>
  resolveTextRule(
    'project.elements.table.cost',
    'Cost per Unit',
    'project.elements.table.cost'
  )
);
const sizeDimensionsStitchCountLabel = computed(() =>
  resolveTextRule(
    'project.elements.table.labor',
    'Size / Dimensions / Stitch Count',
    'project.elements.table.labor'
  )
);
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <div v-if="!hideSave && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))">
      <InputLabel for="element-package" value="Select Element Packages"/>
      <SelectInput id="element-package" v-model="newPackage"
              class="mt-1 mr-5 disabled:bg-gray-50">
        <option :value="null">Please Select</option>
        <option v-for="elementPackage in elementPackages" :value="elementPackage.pack_id"
                :key="elementPackage.pack_id + selectedPackages.length"
                v-text="elementPackage.pack_name"
                :disabled="selectedPackages.includes(elementPackage.pack_id)"></option>
      </SelectInput>
      <PrimaryButton v-if="newPackage !== null" @click.prevent="addNewPackage()">Add Package</PrimaryButton>
    </div>

    <div v-if="project.element_packages.length > 0">
      <div v-for="elementPackage in project.element_packages">
        <p class="mt-10"><b>Package Name: </b><span v-text="elementPackage.pack_name"></span>
          <button class="ml-5 fa fa-trash" @click.prevent="openDeletePackageModal(elementPackage)"
                  v-if="!hideSave && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))"></button>
        </p>
        <table class="min-w-full w-full text-left text-sm font-light">
          <thead class="border-b font-medium dark:border-neutral-500">
          <tr>
            <th scope="col" class="px-4 py-2">Image</th>
            <th scope="col" class="px-4 py-2">File</th>
            <th scope="col" class="px-4 py-2">{{ repairTypeLabel }}</th>
            <th scope="col" class="px-4 py-2">Vendor</th>
            <th scope="col" class="px-4 py-2">Style</th>
            <th scope="col" class="px-4 py-2">Color</th>
            <th scope="col" class="px-4 py-2">Cost</th>
            <th scope="col" class="px-4 py-2">Location</th>
            <th scope="col" class="px-4 py-2">Yield</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="element in elementPackage.elements"
              class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
              :key="element.element_id">
            <td class="px-4 py-2 break-words">
              <img
                class="w-16 cursor-pointer"
                :src="element.all_image_media?.[0]?.url"
                :alt="element.all_image_media?.[0]?.file_name"
                v-if="element.all_image_media?.length"
                @click="openPreviewModal(element.all_image_media[0])"
              />
            </td>
            <td class="px-4 py-2 break-words">
              <a :href="elementImageUrl(element.file)" :title="`Download ${element.file}`" v-if="element.file">
                <span class="mt-2 fa fa-file-download text-5xl block"></span>
              </a>
              <span class="mt-2" v-text="element.file"></span>
            </td>
            <td class="px-4 py-2 break-words" v-text="element.element_type"></td>
            <td class="px-4 py-2 break-words" v-text="element.vendor?.vendorName"></td>
            <td class="px-4 py-2 break-words" v-text="element.style"></td>
            <td class="px-4 py-2 break-words" v-text="element.color"></td>
            <td class="px-4 py-2 break-words" v-text="element.cost ? parseFloat(element.cost).toFixed(2) : '-'"></td>
            <td class="px-4 py-2 break-words" v-text="element.location"></td>
            <td class="px-4 py-2 break-words" v-text="element.yield"></td>
          </tr>
          </tbody>
        </table>

      </div>
    </div>
    <p v-if="project.element_packages.length === 0">
      <span class="block font-bold mb-2 text-lg">Element Packages</span>
      No records...
    </p>
  </div>

  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <table class="min-w-full w-full text-left text-sm font-light">
      <thead class="border-b font-medium dark:border-neutral-500">
      <tr>
        <th scope="col" class="px-4 py-2" v-if="(helpers.userCanManageProject(currentUser))">Image</th>
        <th scope="col" class="px-4 py-2">{{ repairTypeLabel }}</th>
        <th scope="col" class="px-4 py-2">Location</th>
        <th scope="col" class="px-4 py-2">Vendor</th>
        <th scope="col" class="px-4 py-2">Quantity</th>
        <th scope="col" class="px-4 py-2">{{ styleSkuLabel }}</th>
        <th scope="col" class="px-4 py-2">Color</th>
        <th scope="col" class="px-4 py-2">{{ costPerUnitLabel }}</th>
        <th scope="col" class="px-4 py-2">{{ sizeDimensionsStitchCountLabel }}</th>
        <th scope="col" class="px-4 py-2">Order Date</th>
        <th scope="col" class="px-4 py-2">Confirm #</th>
        <th scope="col" class="px-4 py-2">Tracking #</th>
        <th scope="col" class="px-4 py-2">Delivered</th>
        <th scope="col" class="px-4 py-2">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="element in project.elements"
          class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
          :key="element.element_id">
        <td class="px-4 py-2 break-words">
          <img
                class="w-16 cursor-pointer"
                :src="element.all_image_media?.[0]?.url"
                :alt="element.all_image_media?.[0]?.file_name"
                v-if="element.all_image_media?.length"
                @click="openPreviewModal(element.all_image_media[0])"
              />
        </td>
        <td class="px-4 py-2 break-words" v-text="element.element_type.elements"></td>
        <td class="px-4 py-2 break-words" v-text="element.location"></td>
        <td class="px-4 py-2 break-words" v-text="element.vendor?.vendorName"></td>
        <td class="px-4 py-2 break-words" v-text="element.elem_quanity"></td>
        <td class="px-4 py-2 break-words" v-text="element.style"></td>
        <td class="px-4 py-2 break-words" v-text="element.color"></td>
        <td class="px-4 py-2 break-words"
            v-text="element.element_cost ? parseFloat(element.element_cost).toFixed(2) : '-'"></td>
        <td class="px-4 py-2 break-words" v-text="element.element_labor"></td>
        <td class="px-4 py-2 break-words" v-text="element.order_date"></td>
        <td class="px-4 py-2 break-words" v-text="element.elem_conf_num"></td>
        <td class="px-4 py-2 break-words" v-text="element.elem_track_num"></td>
        <td class="px-4 py-2 break-words text-capitalize" v-text="element.elem_delivered"></td>
        <td class="px-4 py-2 break-words">
          <button class="fa fa-pen p-1" title="Edit Element" @click.prevent="openEditElementModal(element)"
                  v-if="!hideSave && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))"></button>
          <button class="fa fa-trash p-1" title="Delete Element" @click.prevent="openDeleteElementModal(element)"
                  v-if="!hideSave && helpers.userCanManageProject(currentUser)"></button>
        </td>
      </tr>
      </tbody>
    </table>
    <p v-if="project.elements.length === 0">
      <span class="block font-bold mb-2 text-lg">{{ repairElementsLabel }}</span>
      No records...
    </p>
  </div>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md"
       v-if="!hideSave && editing === null && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))">
    <EditProjectElementForm :hide-save="hideSave" :vendors="vendors" :project="project"
                            @saving="updateSaving"
                            :element-types="elementTypes"
                            :clicked-save-form="clickedSaveForm"
                            :isClosedProject="props.isClosedProject"
    ></EditProjectElementForm>
  </div>
  <div v-if="hideSave === false"
       class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton @click.prevent="saveForm()" :disabled="saving">Save</PrimaryButton>
  </div>
  <DialogModal :show="preview !== null" @close="closePreviewModal" max-width="7xl">
    <template #title>
      <b v-text="preview.file_name"></b>
    </template>

    <template #content>
      <img :src="preview.url" :alt="preview.file_name">
    </template>
  </DialogModal>
  <DialogModal :show="editing !== null" @close="closeEditElementModal">
    <template #title>
      Edit Element
    </template>

    <template #content>
      <EditProjectElementForm :hide-save="hideSave" :vendors="vendors" :project="project"
                              @saving="updateSaving"
                              :element-types="elementTypes"
                              :clicked-save-form="clickedSaveForm"
                              :element="editing"
                              :isClosedProject="props.isClosedProject"
      ></EditProjectElementForm>
    </template>
    <template #footer>
      <PrimaryButton @click.prevent="saveForm()" :disabled="saving" v-if="hideSave === false">Save</PrimaryButton>
    </template>
  </DialogModal>

  <DialogModal :show="deletingPackage !== null" @close="closeDeletePackageModal">
    <template #title>
      <b>Please confirm</b>
    </template>

    <template #content>
      You are about to delete <b v-text="deletingPackage.pack_name"></b>.
      <br>Are you sure?
    </template>

    <template #footer>
      <SecondaryButton @click="" class="mr-2">Cancel</SecondaryButton>
      <PrimaryButton @click="deleteElementPackage(deletingPackage)">Confirm</PrimaryButton>
    </template>
  </DialogModal>

  <DialogModal :show="deletingElement !== null" @close="closeDeleteElementModal">
    <template #title>
      <b>Please confirm</b>
    </template>

    <template #content>
      You are about to delete this element?
      <br>Are you sure?
    </template>

    <template #footer>
      <SecondaryButton @click="" class="mr-2">Cancel</SecondaryButton>
      <PrimaryButton @click="deleteElement(deletingElement)">Confirm</PrimaryButton>
    </template>
  </DialogModal>
</template>
